/**
 * Centralised filter primitives shared by every queue / module in OneDesk 360.
 *
 * Returns {date_from, date_to} as YYYY-MM-DD strings so it lines up with the
 * backend `/api/exports/*` and `/api/tasks` filters. Times are interpreted in
 * the user's local TZ but the engine stores UTC — close enough for "today /
 * this month" filters in a single-region (IST) dealership.
 */

const MONTH_CODES = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                     "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function pad2(n) { return String(n).padStart(2, "0"); }

export function ymd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function monthLabel(d) {
  return `${MONTH_CODES[d.getMonth()]}_${d.getFullYear()}`;
}

export function rangeForPreset(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today": {
      const s = ymd(today);
      return { date_from: s, date_to: s, label: "Today" };
    }
    case "yesterday": {
      const y = new Date(today); y.setDate(today.getDate() - 1);
      const s = ymd(y);
      return { date_from: s, date_to: s, label: "Yesterday" };
    }
    case "this_week": {
      // Week starts Monday
      const dow = (today.getDay() + 6) % 7;
      const start = new Date(today); start.setDate(today.getDate() - dow);
      return { date_from: ymd(start), date_to: ymd(today), label: "This Week" };
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      // End of the CURRENT calendar month — matches DateRanges.thisMonth()
      // used by Dashboard & Reports so all three surfaces agree even when
      // "today" is not the last day of the month.
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { date_from: ymd(start), date_to: ymd(end), label: "This Month" };
    }
    default:
      return { date_from: null, date_to: null, label: null };
  }
}

export function rangeForMonth(year, month0) {
  // month0 is 0-indexed
  const start = new Date(year, month0, 1);
  const end = new Date(year, month0 + 1, 0);
  return { date_from: ymd(start), date_to: ymd(end), label: `${MONTH_CODES[month0]}_${year}` };
}

export function lastNMonths(n = 12) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i += 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ year: dt.getFullYear(), month0: dt.getMonth(),
               label: `${MONTH_CODES[dt.getMonth()]} ${dt.getFullYear()}` });
  }
  return out;
}

export const SLA_OPTIONS = [
  { value: "on_track", label: "On Track" },
  { value: "at_risk", label: "At Risk" },
  { value: "delayed", label: "Delayed" },
  { value: "on_hold", label: "On Hold" },
  { value: "paused", label: "SLA Paused" },
];

export const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
];

export const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
];

export function defaultFilters() {
  return {
    preset: null,           // today | this_week | this_month
    date_from: null, date_to: null,
    status: null,           // any string — caller controls option set
    sla_status: null,       // on_track | at_risk | delayed | on_hold | paused
    location_id: null,      // narrows within user's allowed locations
    dse_id: null,           // narrows by Sales Executive
    search: "",
  };
}

export function activeFilterCount(f) {
  let n = 0;
  if (f.preset) n += 1;
  if (!f.preset && (f.date_from || f.date_to)) n += 1;
  if (f.status) n += 1;
  if (f.sla_status) n += 1;
  if (f.location_id) n += 1;
  if (f.dse_id) n += 1;
  if ((f.search || "").trim()) n += 1;
  return n;
}

export function buildApiParams(f) {
  const out = {};
  if (f.date_from) out.date_from = f.date_from;
  if (f.date_to) out.date_to = f.date_to;
  if (f.status) out.status = f.status;
  if (f.sla_status) out.sla_status = f.sla_status;
  if (f.dse_id) out.dse_id = f.dse_id;
  if ((f.search || "").trim()) out.search = f.search.trim();
  // Note: location_id is consumed by the caller (it merges with the
  // user's allowed locations). It is NOT forwarded as-is to keep
  // RBAC enforcement at the data fetch layer.
  return out;
}

/**
 * Build an export filename's {scope} + {month_label} segments from the
 * current location + filter state. Centralised so every queue produces a
 * filename that matches the spec (e.g. Insurance_Pending_JEY_MAY_2026.xlsx).
 */
const _MONTH_CODES = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export function deriveScope({ locFilter, visibleLocs, allLocations }) {
  if (locFilter && locFilter !== "all") {
    return visibleLocs.find((l) => l.id === locFilter)?.code || "ALL";
  }
  // location-scoped user (e.g. DSE) effectively sees a single location → use it
  if (visibleLocs?.length === 1) return visibleLocs[0].code;
  // Admin / multi-location user with full visibility → keep filename short
  if (!allLocations || (visibleLocs && visibleLocs.length >= allLocations.length)) {
    return "ALL";
  }
  // True partial selection — concat codes
  return visibleLocs.map((l) => l.code).join("-");
}

export function deriveMonthLabel(f) {
  const iso = f.date_to || f.date_from;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${_MONTH_CODES[d.getMonth()]}_${d.getFullYear()}`;
}

export { MONTH_CODES };
